/* ==========================================================================
   SERPROMAQ - CONTROLADOR DEL PANEL DE ADMINISTRACIÓN (DASHBOARD)
   ========================================================================== */

import { 
  auth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from './firebase-config.js';
import { 
  getLoadedCatalogItems, 
  subscribeToCatalog, 
  saveProduct, 
  deleteProduct, 
  updateProductAvailability, 
  uploadProductImage, 
  seedInitialCatalog 
} from './catalog-service.js';

let currentUser = null;
let currentAdminFilter = 'todos';
let currentAdminSearch = '';
let editingProductId = null;
let selectedImageFile = null;

/**
 * Inicializar el Dashboard de Administración
 */
export function initAdminDashboard() {
  injectAdminMarkup();
  setupAuthListener();
  setupGlobalAdminTriggers();
  setupEditorModal();
}

/**
 * Inyectar el HTML del Dashboard y Editor en el DOM
 */
function injectAdminMarkup() {
  if (document.getElementById('adminModalOverlay')) return;

  const adminWrapper = document.createElement('div');
  adminWrapper.innerHTML = `
    <!-- Overlay del Dashboard Principal -->
    <div class="admin-modal-overlay" id="adminModalOverlay">
      <div class="admin-panel-container">
        <!-- Header -->
        <div class="admin-header">
          <div class="admin-header-title">
            <i class="fa-solid fa-boxes-packing"></i>
            <div>
              <h2>Panel de Control de Inventario</h2>
              <span>SERPROMAQ S.A.S. • Gestión Comercial en Tiempo Real</span>
            </div>
          </div>
          <div class="admin-header-actions">
            <div class="admin-user-pill" id="adminUserPill" style="display: none;">
              <i class="fa-solid fa-user-shield"></i>
              <span id="adminUserEmail">admin@serpromaq.com</span>
            </div>
            <button type="button" class="btn-admin-secondary" id="btnAdminLogout" style="display: none; padding: 0.4rem 0.85rem; font-size: 0.775rem;">
              <i class="fa-solid fa-right-from-bracket"></i> Salir
            </button>
            <button type="button" class="btn-admin-close" id="btnAdminClose" title="Cerrar Panel">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        <!-- Cuerpo del Dashboard -->
        <div class="admin-body" id="adminBody">
          <!-- Se renderiza dinámicamente: Login View o Dashboard View -->
        </div>
      </div>
    </div>

    <!-- Modal de Crear / Editar Producto -->
    <div class="admin-editor-overlay" id="adminEditorOverlay">
      <div class="admin-editor-card">
        <div class="admin-editor-header">
          <h3 id="editorModalTitle" style="margin: 0; font-size: 1.2rem; font-weight: 800;">
            <i class="fa-solid fa-pen-to-square" style="color: var(--color-primary);"></i> Nuevo Equipo / Producto
          </h3>
          <button type="button" class="btn-admin-close" id="btnEditorClose">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <form id="productEditorForm" class="admin-editor-body">
          <input type="hidden" id="editProductId" value="" />

          <div class="admin-form-grid-2">
            <div class="form-group">
              <label class="form-label" for="editTitle">Nombre / Título del Equipo</label>
              <input type="text" id="editTitle" class="form-input" placeholder="Ej. Montacargas Eléctrico Lithium 2.5 Tn" />
            </div>

            <div class="form-group">
              <label class="form-label" for="editCategory">Categoría del Catálogo</label>
              <select id="editCategory" class="form-select">
                <option value="venta">Venta de Maquinaria</option>
                <option value="alquiler">Alquiler Flexible</option>
                <option value="mantenimiento">Taller & Servicio Técnico</option>
                <option value="repuestos">Insumos & Repuestos</option>
                <option value="otro">Otro / Personalizado</option>
              </select>
              <div id="customCategoryWrapper" style="display: none; margin-top: 0.45rem;">
                <input type="text" id="editCategoryCustom" class="form-input" placeholder="Escribe el nombre de la categoría personalizada..." />
              </div>
            </div>
          </div>

          <div class="admin-form-grid-2">
            <div class="form-group">
              <label class="form-label" for="editBadgeText">Etiqueta Superior (Badge)</label>
              <input type="text" id="editBadgeText" class="form-input" placeholder="Ej. NUEVO • LITIO, OFERTA, ENTREGA INMEDIATA" />
            </div>

            <div class="form-group">
              <label class="form-label" for="editHighlightTag">Tag de Características</label>
              <input type="text" id="editHighlightTag" class="form-input" placeholder="Ej. ⚡ Batería Litio 80V, 🔥 Motor K25" />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="editAvailability">Disponibilidad Inicial</label>
            <select id="editAvailability" class="form-select">
              <option value="Disponible en Stock">Disponible en Stock</option>
              <option value="Entrega Inmediata">Entrega Inmediata</option>
              <option value="Despacho en 24h">Despacho en 24h</option>
              <option value="Bajo Pedido">Bajo Pedido</option>
              <option value="Vendido / Fuera de Stock">Vendido / Fuera de Stock</option>
            </select>
          </div>

          <!-- Subida de Imagen -->
          <div class="form-group" style="margin-top: 0.5rem;">
            <label class="form-label">Fotografía del Producto (Opcional)</label>
            <div class="admin-dropzone" id="adminImageDropzone">
              <i class="fa-solid fa-cloud-arrow-up" style="font-size: 2rem; color: var(--color-primary); margin-bottom: 0.5rem;"></i>
              <strong style="display: block; font-size: 0.9rem; color: #0F172A;">Seleccionar o arrastrar fotografía</strong>
              <span style="font-size: 0.75rem; color: #64748B;">JPG, PNG, WebP (Opcional - Máx. 5MB)</span>
              <input type="file" id="editImageFileInput" accept="image/*" style="display: none;" />
            </div>
            
            <div id="imagePreviewWrapper" style="display: none; margin-top: 0.75rem;">
              <img id="imagePreviewImg" src="" alt="Previsualización" class="admin-preview-img" />
              <input type="hidden" id="editImageUrl" value="" />
            </div>
          </div>

          <!-- Especificaciones Técnicas (4 campos rápidos) -->
          <div style="margin-top: 1rem; background: #F1F5F9; padding: 1rem; border-radius: 12px; border: 1px solid #E2E8F0;">
            <strong style="display: block; font-size: 0.85rem; color: #0F172A; margin-bottom: 0.75rem;">
              <i class="fa-solid fa-sliders"></i> Especificaciones Técnicas Clave (Visibles en la tarjeta)
            </strong>
            <div class="admin-form-grid-2">
              <div class="form-group">
                <label class="form-label" style="font-size: 0.75rem;">Capacidad / Carga</label>
                <input type="text" id="specCapacidad" class="form-input" placeholder="Ej. 2,500 kg (2.5 Tn)" />
              </div>
              <div class="form-group">
                <label class="form-label" style="font-size: 0.75rem;">Altura / Mástil</label>
                <input type="text" id="specAltura" class="form-input" placeholder="Ej. 4.80 m (Torre Triplex)" />
              </div>
              <div class="form-group">
                <label class="form-label" style="font-size: 0.75rem;">Motor / Potencia / Tipo</label>
                <input type="text" id="specMotor" class="form-input" placeholder="Ej. Eléctrico AC Lithium 80V" />
              </div>
              <div class="form-group">
                <label class="form-label" style="font-size: 0.75rem;">Garantía / Modalidad</label>
                <input type="text" id="specGarantia" class="form-input" placeholder="Ej. 3 Años de Garantía Total" />
              </div>
            </div>
          </div>

          <!-- Descripción Comercial -->
          <div class="form-group" style="margin-top: 1rem;">
            <label class="form-label" for="editDescription">Descripción Comercial</label>
            <textarea id="editDescription" class="form-textarea" rows="3" placeholder="Describe los beneficios principales del equipo o servicio (Opcional)..."></textarea>
          </div>

          <!-- Ventajas Destacadas (Ficha Técnica & Quick View) -->
          <div class="form-group" style="margin-top: 1rem; background: #F8FAFC; padding: 1rem; border-radius: 12px; border: 1px solid #E2E8F0;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.45rem;">
              <label class="form-label" for="editFeatures" style="margin-bottom: 0; font-weight: 800; color: #0F172A;">
                <i class="fa-solid fa-circle-check" style="color: #10B981;"></i> Ventajas Destacadas (Ficha Técnica)
              </label>
              <span style="font-size: 0.725rem; color: #64748B; font-weight: 600;">(Una ventaja por cada línea)</span>
            </div>
            <textarea id="editFeatures" class="form-textarea" rows="4" placeholder="Ejemplo:&#10;Batería de litio de carga ultra rápida (1.5 horas al 100%)&#10;Cero emisiones de CO2 y mínimo nivel sonoro para bodegas&#10;Mástil de alta visibilidad y pantalla LCD de control inteligente&#10;Frenos regenerativos de alta eficiencia y bajo costo operativo"></textarea>
            <span style="font-size: 0.725rem; color: #94A3B8; display: block; margin-top: 0.35rem;">
              💡 Cada línea escrita se convertirá en una viñeta con icono verde en la Ficha Técnica del producto.
            </span>
          </div>

          <!-- Botones de Acción del Formulario -->
          <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #E2E8F0;">
            <button type="button" class="btn-admin-secondary" id="btnCancelEditor">
              Cancelar
            </button>
            <button type="submit" class="btn-admin-add" id="btnSaveProduct">
              <i class="fa-solid fa-floppy-disk"></i> Guardar Producto
            </button>
          </div>
        </form>
      </div>
    </div>
  `;

  document.body.appendChild(adminWrapper);
}

/**
 * Configurar listeners de autenticación
 */
const ADMIN_PRIMARY_EMAIL = 'postulaciones@serpromaqsas.com';
const ADMIN_SESSION_KEY = 'serpromaq_admin_session_auth';

// Estado interno de autenticación de dos pasos (2FA)
let loginStep = 1; // 1: Credenciales, 2: Código OTP
let tempAuthEmail = ADMIN_PRIMARY_EMAIL;
let otpResendCountdown = 0;
let otpCountdownTimer = null;

/**
 * Configurar listeners de autenticación
 */
function setupAuthListener() {
  // Verificar si hay sesión administrativa activa previamente validada
  const savedAdminSession = localStorage.getItem(ADMIN_SESSION_KEY);
  if (savedAdminSession) {
    try {
      const parsedSession = JSON.parse(savedAdminSession);
      if (parsedSession && parsedSession.email && (Date.now() - parsedSession.timestamp < 24 * 60 * 60 * 1000)) {
        currentUser = { email: parsedSession.email, token: parsedSession.token };
      } else {
        localStorage.removeItem(ADMIN_SESSION_KEY);
      }
    } catch (e) {
      localStorage.removeItem(ADMIN_SESSION_KEY);
    }
  }

  onAuthStateChanged(auth, (user) => {
    if (user) {
      currentUser = user;
    }
    updateAdminUiState();
  });

  const logoutBtn = document.getElementById('btnAdminLogout');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      localStorage.removeItem(ADMIN_SESSION_KEY);
      currentUser = null;
      loginStep = 1;
      try {
        await signOut(auth);
      } catch (e) {}
      updateAdminUiState();
      showToast('Sesión de administrador cerrada correctamente', 'info');
    });
  }
}

function updateAdminUiState() {
  const userPill = document.getElementById('adminUserPill');
  const userEmail = document.getElementById('adminUserEmail');
  const logoutBtn = document.getElementById('btnAdminLogout');

  if (currentUser) {
    if (userPill) userPill.style.display = 'inline-flex';
    if (userEmail) userEmail.textContent = currentUser.email || ADMIN_PRIMARY_EMAIL;
    if (logoutBtn) logoutBtn.style.display = 'inline-flex';
    renderDashboardView();
  } else {
    if (userPill) userPill.style.display = 'none';
    if (logoutBtn) logoutBtn.style.display = 'none';
    renderLoginView();
  }
}

/**
 * Enviar notificación por correo administrativo y generar código de seguridad 2FA
 */
async function requestAdminOtp(email) {
  // 1. Generar código numérico criptográfico de 6 dígitos
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  const otpCode = String(100000 + (array[0] % 900000));
  const expiresAt = Date.now() + (10 * 60 * 1000); // 10 minutos

  // 2. Guardar en Firestore para verificación en tiempo real en la nube
  try {
    const otpDocRef = doc(db, 'system_security', 'admin_otp');
    await setDoc(otpDocRef, {
      code: otpCode,
      email: email.toLowerCase(),
      expiresAt: expiresAt,
      createdAt: serverTimestamp(),
      attempts: 0
    });
  } catch (firestoreErr) {
    console.warn('Registro en Firestore:', firestoreErr);
  }

  // 3. Enviar notificación directa al correo postulaciones@serpromaqsas.com con el código
  try {
    const endpointUrl = window.location.hostname.includes('serpromaqsas.com')
      ? '/send_application.php'
      : 'https://serpromaqsas.com/send_application.php';

    const formData = new FormData();
    formData.append('type', 'seguridad');
    formData.append('name', 'Sistema de Seguridad 2FA');
    formData.append('phone', 'PBX Administrativo');
    formData.append('email', email);
    formData.append('role', 'Código de Verificación - Panel de Inventario');
    formData.append('experience', `Código Generado: ${otpCode} (Expira en 10 min)`);
    formData.append('brands', `Código confidencial: ${otpCode}. Si no solicitaste este acceso, ignora este mensaje.`);

    fetch(endpointUrl, {
      method: 'POST',
      body: formData
    }).catch(e => console.warn('Notificación por correo:', e));
  } catch (mailErr) {
    console.warn('Error en despacho de correo:', mailErr);
  }

  // Guardar en sesión local protegida para contingencia inmediata
  sessionStorage.setItem('serpromaq_pending_otp', JSON.stringify({
    code: otpCode,
    email: email.toLowerCase(),
    expiresAt: expiresAt
  }));

  return { success: true, message: `Código de seguridad generado para ${email}` };
}

/**
 * Validar código OTP de 6 dígitos ingresado por el usuario
 */
async function verifyAdminOtp(email, otpCode) {
  const cleanCode = String(otpCode).trim();
  const cleanEmail = String(email).trim().toLowerCase();

  // Clave maestra de respaldo por seguridad
  if (cleanCode === '940216') {
    return { success: true, token: 'master_superadmin_' + Date.now() };
  }

  // 1. Validar contra Firestore
  try {
    const otpDocRef = doc(db, 'system_security', 'admin_otp');
    const docSnap = await getDoc(otpDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.email === cleanEmail) {
        if (Date.now() > (data.expiresAt || 0)) {
          throw new Error('El código de seguridad ha expirado (límite de 10 min). Solicita uno nuevo.');
        }
        if (data.code === cleanCode) {
          // Código válido: limpiar código usado
          await deleteDoc(otpDocRef).catch(() => {});
          sessionStorage.removeItem('serpromaq_pending_otp');
          return { success: true, token: 'firestore_token_' + Date.now() };
        }
      }
    }
  } catch (err) {
    if (err.message && err.message.includes('expirado')) throw err;
    console.warn('Consulta en Firestore:', err);
  }

  // 2. Fallback con sesión temporal en memoria protegida
  const pendingRaw = sessionStorage.getItem('serpromaq_pending_otp');
  if (pendingRaw) {
    try {
      const pending = JSON.parse(pendingRaw);
      if (pending && pending.email === cleanEmail) {
        if (Date.now() > pending.expiresAt) {
          sessionStorage.removeItem('serpromaq_pending_otp');
          throw new Error('El código de seguridad ha expirado. Solicita uno nuevo.');
        }
        if (pending.code === cleanCode) {
          sessionStorage.removeItem('serpromaq_pending_otp');
          return { success: true, token: 'session_token_' + Date.now() };
        }
      }
    } catch (e) {
      if (e.message && e.message.includes('expirado')) throw e;
    }
  }

  throw new Error('Código de seguridad incorrecto. Revisa el código de 6 dígitos enviado a tu correo.');
}

/**
 * Renderizar Vista de Login con Verificación en Dos Pasos (2FA)
 */
function renderLoginView() {
  const body = document.getElementById('adminBody');
  if (!body) return;

  if (loginStep === 1) {
    // Paso 1: Correo y Contraseña
    body.innerHTML = `
      <div class="admin-login-view">
        <div class="admin-login-icon">
          <i class="fa-solid fa-shield-halved"></i>
        </div>
        
        <h3>Acceso al Panel de Inventario</h3>
        <p>Autenticación segura para administración de equipos y catálogo SERPROMAQ.</p>

        <div class="admin-login-steps-indicator">
          <span class="admin-step-pill active"><i class="fa-solid fa-key"></i> Paso 1: Credenciales</span>
          <span class="admin-step-pill"><i class="fa-solid fa-envelope-circle-check"></i> Paso 2: Código al Correo</span>
        </div>

        <form id="adminLoginForm">
          <div class="form-group" style="text-align: left; margin-bottom: 1.15rem;">
            <label class="form-label" for="loginEmail">Correo Administrativo Autorizado</label>
            <div style="position: relative;">
              <input 
                type="email" 
                id="loginEmail" 
                class="form-input" 
                value="${ADMIN_PRIMARY_EMAIL}" 
                readonly
                style="background: #F8FAFC; color: #0F172A; font-weight: 700; cursor: not-allowed; border-color: #CBD5E1; padding-left: 2.25rem;" 
              />
              <i class="fa-solid fa-lock" style="position: absolute; left: 0.85rem; top: 50%; transform: translateY(-50%); color: #64748B; font-size: 0.85rem;"></i>
            </div>
            <small style="color: #059669; font-size: 0.775rem; margin-top: 0.35rem; display: flex; align-items: center; gap: 0.35rem; font-weight: 600;">
              <i class="fa-solid fa-circle-check"></i> Única cuenta oficial con privilegios de acceso al panel
            </small>
          </div>

          <div class="form-group" style="text-align: left; margin-bottom: 1.5rem;">
            <label class="form-label" for="loginPassword">Contraseña de Administrador</label>
            <input 
              type="password" 
              id="loginPassword" 
              class="form-input" 
              placeholder="••••••••••••" 
              required 
            />
          </div>

          <button type="submit" class="btn-admin-add" style="width: 100%; justify-content: center; padding: 0.85rem;" id="btnLoginSubmit">
            <i class="fa-solid fa-arrow-right"></i> Validar Credenciales & Enviar Código
          </button>
        </form>

        <div style="margin-top: 1.5rem; border-top: 1px solid #E2E8F0; padding-top: 1rem; text-align: center;">
          <small style="color: #94A3B8; font-size: 0.775rem;">
            <i class="fa-solid fa-lock"></i> Sistema de alta seguridad con verificación de identidad por correo
          </small>
        </div>
      </div>
    `;

    const form = document.getElementById('adminLoginForm');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = document.getElementById('loginEmail').value.trim().toLowerCase();
        const pass = document.getElementById('loginPassword').value.trim();
        const submitBtn = document.getElementById('btnLoginSubmit');

        if (email !== ADMIN_PRIMARY_EMAIL.toLowerCase()) {
          showToast(`Acceso denegado. Solo se autoriza: ${ADMIN_PRIMARY_EMAIL}`, 'error');
          return;
        }

        // Validación estricta de contraseña autorizada
        const OFFICIAL_ADMIN_PASSWORD = 'Serpromaq2026*';
        if (pass !== OFFICIAL_ADMIN_PASSWORD) {
          showToast('Contraseña incorrecta. Por favor verifícala.', 'error');
          return;
        }

        try {
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Generando código seguro...';

          tempAuthEmail = email;

          // Intentar autenticación opcional de Firebase
          try {
            await signInWithEmailAndPassword(auth, email, pass);
          } catch (e) {
            // Continúa con la verificación OTP de dos factores
          }

          // Enviar código OTP de 6 dígitos al correo postulaciones@serpromaqsas.com
          await requestAdminOtp(tempAuthEmail);
          
          loginStep = 2;
          startOtpCountdown(60);
          showToast(`¡Código enviado a ${tempAuthEmail}! Revisa tu bandeja de entrada.`, 'success');
          renderLoginView();
        } catch (err) {
          console.error('Error al solicitar código OTP:', err);
          showToast(err.message || 'Error al enviar código de verificación.', 'error');
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = '<i class="fa-solid fa-arrow-right"></i> Validar Credenciales & Enviar Código';
          }
        }
      });
    }

  } else if (loginStep === 2) {
    // Paso 2: Verificación OTP de 6 Dígitos
    body.innerHTML = `
      <div class="admin-login-view">
        <div class="admin-login-icon" style="background: rgba(16, 185, 129, 0.12); color: #10B981;">
          <i class="fa-solid fa-envelope-circle-check"></i>
        </div>
        
        <h3>Verificación de Seguridad (2FA)</h3>
        <p>Hemos enviado un código confidencial de 6 dígitos a:</p>

        <div class="admin-security-banner">
          <i class="fa-solid fa-shield-halved" style="font-size: 1.1rem; margin-top: 2px;"></i>
          <div>
            <strong>Destino Oficial:</strong> <span>${tempAuthEmail}</span><br>
            <span style="font-size: 0.75rem; color: #9A3412;">Ingresa a tu correo administrativo para consultar el código de acceso.</span>
          </div>
        </div>

        <div class="admin-login-steps-indicator">
          <span class="admin-step-pill completed"><i class="fa-solid fa-check"></i> Credenciales OK</span>
          <span class="admin-step-pill active"><i class="fa-solid fa-key"></i> Código OTP</span>
        </div>

        <form id="adminOtpForm">
          <div class="otp-digit-inputs">
            <input type="text" maxlength="1" class="otp-box" data-index="0" autofocus inputmode="numeric" />
            <input type="text" maxlength="1" class="otp-box" data-index="1" inputmode="numeric" />
            <input type="text" maxlength="1" class="otp-box" data-index="2" inputmode="numeric" />
            <input type="text" maxlength="1" class="otp-box" data-index="3" inputmode="numeric" />
            <input type="text" maxlength="1" class="otp-box" data-index="4" inputmode="numeric" />
            <input type="text" maxlength="1" class="otp-box" data-index="5" inputmode="numeric" />
          </div>

          <button type="submit" class="btn-admin-add" style="width: 100%; justify-content: center; padding: 0.85rem; margin-top: 0.5rem;" id="btnOtpSubmit">
            <i class="fa-solid fa-unlock-keyhole"></i> Verificar Código & Abrir Panel
          </button>
        </form>

        <div style="margin-top: 1.25rem; display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem;">
          <button type="button" id="btnBackToStep1" style="background: none; border: none; color: #64748B; cursor: pointer; text-decoration: underline; font-weight: 600;">
            <i class="fa-solid fa-arrow-left"></i> Cambiar correo o clave
          </button>

          <button type="button" id="btnResendOtp" style="background: none; border: none; color: var(--color-primary); cursor: pointer; font-weight: 700;">
            ${otpResendCountdown > 0 ? `Reenviar en ${otpResendCountdown}s` : '<i class="fa-solid fa-rotate-right"></i> Reenviar código'}
          </button>
        </div>
      </div>
    `;

    setupOtpInputs();

    const otpForm = document.getElementById('adminOtpForm');
    const backBtn = document.getElementById('btnBackToStep1');
    const resendBtn = document.getElementById('btnResendOtp');

    if (backBtn) {
      backBtn.addEventListener('click', () => {
        loginStep = 1;
        renderLoginView();
      });
    }

    if (resendBtn && otpResendCountdown <= 0) {
      resendBtn.addEventListener('click', async () => {
        try {
          resendBtn.disabled = true;
          resendBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Reenviando...';
          await requestAdminOtp(tempAuthEmail);
          startOtpCountdown(60);
          showToast(`Nuevo código enviado a ${tempAuthEmail}`, 'success');
          renderLoginView();
        } catch (e) {
          showToast(e.message || 'Error reenviando código.', 'error');
          resendBtn.disabled = false;
        }
      });
    }

    if (otpForm) {
      otpForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const boxes = document.querySelectorAll('.otp-box');
        let fullCode = '';
        boxes.forEach(b => fullCode += b.value.trim());

        if (fullCode.length !== 6) {
          showToast('Por favor escribe el código de 6 dígitos completo.', 'error');
          return;
        }

        const submitBtn = document.getElementById('btnOtpSubmit');
        try {
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Verificando código...';

          const result = await verifyAdminOtp(tempAuthEmail, fullCode);

          // Guardar sesión de administrador validada por 24 horas
          const sessionPayload = {
            email: tempAuthEmail,
            token: result.token || 'valid_2fa_session',
            timestamp: Date.now()
          };
          localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(sessionPayload));
          currentUser = { email: tempAuthEmail, token: result.token };

          loginStep = 1;
          showToast('¡Verificación exitosa! Acceso concedido al Inventario.', 'success');
          updateAdminUiState();
        } catch (err) {
          console.error('Error verificación OTP:', err);
          showToast(err.message || 'Código de seguridad incorrecto.', 'error');
          boxes.forEach(b => {
            b.value = '';
            b.style.borderColor = '#EF4444';
          });
          if (boxes[0]) boxes[0].focus();
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = '<i class="fa-solid fa-unlock-keyhole"></i> Verificar Código & Abrir Panel';
          }
        }
      });
    }
  }
}

/**
 * Control interactivo de las 6 casillas de código OTP
 */
function setupOtpInputs() {
  const boxes = document.querySelectorAll('.otp-box');
  if (boxes.length === 0) return;

  boxes.forEach((box, idx) => {
    box.addEventListener('input', (e) => {
      const val = e.target.value.replace(/[^0-9]/g, '');
      box.value = val ? val[0] : '';
      if (val && idx < boxes.length - 1) {
        boxes[idx + 1].focus();
      }
    });

    box.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !box.value && idx > 0) {
        boxes[idx - 1].focus();
      }
    });

    box.addEventListener('paste', (e) => {
      e.preventDefault();
      const pasteData = (e.clipboardData || window.clipboardData).getData('text').trim().replace(/[^0-9]/g, '');
      if (pasteData) {
        for (let i = 0; i < boxes.length; i++) {
          boxes[i].value = pasteData[i] || '';
        }
        const lastFilled = Math.min(pasteData.length, boxes.length - 1);
        boxes[lastFilled].focus();
      }
    });
  });

  if (boxes[0]) boxes[0].focus();
}

/**
 * Temporizador de reenvío de OTP
 */
function startOtpCountdown(seconds) {
  otpResendCountdown = seconds;
  if (otpCountdownTimer) clearInterval(otpCountdownTimer);

  otpCountdownTimer = setInterval(() => {
    otpResendCountdown--;
    const resendBtn = document.getElementById('btnResendOtp');
    if (resendBtn) {
      if (otpResendCountdown > 0) {
        resendBtn.textContent = `Reenviar en ${otpResendCountdown}s`;
        resendBtn.style.pointerEvents = 'none';
        resendBtn.style.opacity = '0.6';
      } else {
        resendBtn.innerHTML = '<i class="fa-solid fa-rotate-right"></i> Reenviar código';
        resendBtn.style.pointerEvents = 'auto';
        resendBtn.style.opacity = '1';
        clearInterval(otpCountdownTimer);
      }
    } else {
      clearInterval(otpCountdownTimer);
    }
  }, 1000);
}

/**
 * Triggers globales (Hash #admin o botones en el footer)
 */
function setupGlobalAdminTriggers() {
  const overlay = document.getElementById('adminModalOverlay');
  const closeBtn = document.getElementById('btnAdminClose');

  function openAdmin() {
    if (overlay) {
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeAdmin() {
    if (overlay) {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
      if (window.location.hash === '#admin') {
        history.pushState(null, '', window.location.pathname);
      }
    }
  }

  if (closeBtn) closeBtn.addEventListener('click', closeAdmin);

  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeAdmin();
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay?.classList.contains('active')) {
      closeAdmin();
    }
  });

  // Listener para enlaces con href="#admin"
  document.querySelectorAll('a[href="#admin"], #btnOpenAdmin').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openAdmin();
    });
  });

  // Abrir si la URL contiene #admin
  if (window.location.hash === '#admin') {
    openAdmin();
  }
}

/**
 * Renderizar Vista Principal del Dashboard
 */
function renderDashboardView() {
  const body = document.getElementById('adminBody');
  if (!body) return;

  const items = getLoadedCatalogItems();
  const totalCount = items.length;
  const inStockCount = items.filter(i => (i.availability || '').toLowerCase().includes('stock')).length;
  const rentCount = items.filter(i => i.category === 'alquiler').length;
  const pausedCount = items.filter(i => (i.availability || '').toLowerCase().includes('vendido') || (i.availability || '').toLowerCase().includes('fuera')).length;

  body.innerHTML = `
    <!-- Métricas Rápidas -->
    <div class="admin-metrics-row">
      <div class="admin-metric-card">
        <div class="admin-metric-icon total"><i class="fa-solid fa-boxes-stacked"></i></div>
        <div class="admin-metric-text">
          <strong>${totalCount}</strong>
          <span>Total Soluciones</span>
        </div>
      </div>
      <div class="admin-metric-card">
        <div class="admin-metric-icon stock"><i class="fa-solid fa-circle-check"></i></div>
        <div class="admin-metric-text">
          <strong>${inStockCount}</strong>
          <span>En Stock Activo</span>
        </div>
      </div>
      <div class="admin-metric-card">
        <div class="admin-metric-icon rent"><i class="fa-solid fa-calendar-check"></i></div>
        <div class="admin-metric-text">
          <strong>${rentCount}</strong>
          <span>Equipos de Alquiler</span>
        </div>
      </div>
      <div class="admin-metric-card">
        <div class="admin-metric-icon paused"><i class="fa-solid fa-ban"></i></div>
        <div class="admin-metric-text">
          <strong>${pausedCount}</strong>
          <span>Vendidos / Pausados</span>
        </div>
      </div>
    </div>

    <!-- Barra de Herramientas -->
    <div class="admin-toolbar">
      <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
        <button class="catalog-tab ${currentAdminFilter === 'todos' ? 'active' : ''}" data-admin-filter="todos">
          Todos (${totalCount})
        </button>
        <button class="catalog-tab ${currentAdminFilter === 'venta' ? 'active' : ''}" data-admin-filter="venta">
          Venta (${items.filter(i => i.category === 'venta').length})
        </button>
        <button class="catalog-tab ${currentAdminFilter === 'alquiler' ? 'active' : ''}" data-admin-filter="alquiler">
          Alquiler (${rentCount})
        </button>
        <button class="catalog-tab ${currentAdminFilter === 'mantenimiento' ? 'active' : ''}" data-admin-filter="mantenimiento">
          Taller (${items.filter(i => i.category === 'mantenimiento').length})
        </button>
        <button class="catalog-tab ${currentAdminFilter === 'repuestos' ? 'active' : ''}" data-admin-filter="repuestos">
          Repuestos (${items.filter(i => i.category === 'repuestos').length})
        </button>
      </div>

      <div class="admin-toolbar-actions">
        <div class="catalog-search-box" style="width: 240px; padding: 0.4rem 0.85rem;">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input type="text" id="adminSearchInput" placeholder="Filtrar inventario..." value="${currentAdminSearch}" />
        </div>

        <button type="button" class="btn-admin-add" id="btnOpenNewProductModal">
          <i class="fa-solid fa-plus"></i> Nuevo Equipo
        </button>

        <button type="button" class="btn-admin-secondary" id="btnSyncSeedData" title="Cargar los 11 productos base si la base de datos está vacía">
          <i class="fa-solid fa-cloud-arrow-up"></i> Sincronizar Base
        </button>
      </div>
    </div>

    <!-- Tabla de Productos -->
    <div class="admin-table-wrapper">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Equipo / Producto</th>
            <th>Categoría</th>
            <th>Disponibilidad (1-Clic)</th>
            <th style="text-align: right;">Acciones</th>
          </tr>
        </thead>
        <tbody id="adminTableBody">
          <!-- Renderizado dinámico -->
        </tbody>
      </table>
    </div>
  `;

  renderAdminTableRows();
  setupDashboardEvents();
}

/**
 * Renderizar las filas de la tabla de administración
 */
function renderAdminTableRows() {
  const tbody = document.getElementById('adminTableBody');
  if (!tbody) return;

  const allItems = getLoadedCatalogItems();
  const filtered = allItems.filter(item => {
    const matchesCat = currentAdminFilter === 'todos' || item.category === currentAdminFilter;
    if (!currentAdminSearch) return matchesCat;
    const q = currentAdminSearch.toLowerCase();
    return matchesCat && (item.title.toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q));
  });

  if (filtered.length === 0) {
    tbody.innerHTML = `
      <tr>
        <td colspan="4" style="text-align: center; padding: 2.5rem; color: #64748B;">
          <i class="fa-solid fa-box-open" style="font-size: 2rem; color: #CBD5E1; margin-bottom: 0.5rem; display: block;"></i>
          No hay productos que coincidan con la búsqueda o categoría seleccionada.
        </td>
      </tr>
    `;
    return;
  }

  tbody.innerHTML = filtered.map(item => {
    const status = item.availability || 'Disponible en Stock';
    const statusClass = status.includes('Stock') ? 'status-in-stock'
      : status.includes('Inmediata') ? 'status-immediate'
      : status.includes('Vendido') || status.includes('Fuera') ? 'status-sold'
      : 'status-custom';

    return `
      <tr>
        <td>
          <div class="admin-product-cell">
            <img src="${item.image}" alt="${item.title}" class="admin-product-thumb" />
            <div class="admin-product-info">
              <strong>${item.title}</strong>
              <span>${item.highlightTag || item.badgeText || 'SERPROMAQ'}</span>
            </div>
          </div>
        </td>
        <td>
          <span class="catalog-category-tag">${item.category.toUpperCase()}</span>
        </td>
        <td>
          <select class="admin-status-select ${statusClass}" data-product-id="${item.id}">
            <option value="Disponible en Stock" ${status === 'Disponible en Stock' ? 'selected' : ''}>● Disponible en Stock</option>
            <option value="Entrega Inmediata" ${status === 'Entrega Inmediata' ? 'selected' : ''}>● Entrega Inmediata</option>
            <option value="Despacho en 24h" ${status === 'Despacho en 24h' ? 'selected' : ''}>● Despacho en 24h</option>
            <option value="Bajo Pedido" ${status === 'Bajo Pedido' ? 'selected' : ''}>● Bajo Pedido</option>
            <option value="Vendido / Fuera de Stock" ${status === 'Vendido / Fuera de Stock' ? 'selected' : ''}>✕ Vendido / Fuera de Stock</option>
          </select>
        </td>
        <td style="text-align: right;">
          <div class="admin-row-actions" style="justify-content: flex-end;">
            <button type="button" class="btn-action-edit" data-edit-id="${item.id}">
              <i class="fa-solid fa-pen"></i> Editar
            </button>
            <button type="button" class="btn-action-delete" data-delete-id="${item.id}" data-delete-title="${item.title}">
              <i class="fa-solid fa-trash-can"></i>
            </button>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  // Listeners de cambio de disponibilidad 1-clic
  tbody.querySelectorAll('.admin-status-select').forEach(select => {
    select.addEventListener('change', async (e) => {
      const productId = e.target.getAttribute('data-product-id');
      const newStatus = e.target.value;
      try {
        await updateProductAvailability(productId, newStatus);
        showToast(`Disponibilidad actualizada a "${newStatus}"`, 'success');
        renderDashboardView();
      } catch (err) {
        showToast('Error al actualizar disponibilidad', 'error');
      }
    });
  });

  // Listeners de Edición
  tbody.querySelectorAll('.btn-action-edit').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-edit-id');
      openProductEditor(id);
    });
  });

  // Listeners de Eliminación
  tbody.querySelectorAll('.btn-action-delete').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-delete-id');
      const title = btn.getAttribute('data-delete-title');
      if (confirm(`¿Estás seguro de que deseas eliminar "${title}" del catálogo?`)) {
        try {
          await deleteProduct(id);
          showToast(`"${title}" fue eliminado del catálogo`, 'info');
          renderDashboardView();
        } catch (err) {
          showToast('Error al eliminar producto', 'error');
        }
      }
    });
  });
}

/**
 * Eventos del Dashboard (Filtros, Buscador, Seed)
 */
function setupDashboardEvents() {
  const filterBtns = document.querySelectorAll('[data-admin-filter]');
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      currentAdminFilter = btn.getAttribute('data-admin-filter');
      renderDashboardView();
    });
  });

  const searchInput = document.getElementById('adminSearchInput');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      currentAdminSearch = e.target.value.trim();
      renderAdminTableRows();
    });
  }

  const addBtn = document.getElementById('btnOpenNewProductModal');
  if (addBtn) {
    addBtn.addEventListener('click', () => openProductEditor());
  }

  const seedBtn = document.getElementById('btnSyncSeedData');
  if (seedBtn) {
    seedBtn.addEventListener('click', async () => {
      if (confirm('¿Deseas sincronizar los 11 productos predeterminados hacia la base de datos en la nube?')) {
        try {
          seedBtn.disabled = true;
          seedBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sincronizando...';
          const res = await seedInitialCatalog();
          showToast(`¡Se sincronizaron ${res.count} productos exitosamente!`, 'success');
          renderDashboardView();
        } catch (err) {
          showToast('Error al sincronizar base de datos', 'error');
        } finally {
          seedBtn.disabled = false;
          seedBtn.innerHTML = '<i class="fa-solid fa-cloud-arrow-up"></i> Sincronizar Base';
        }
      }
    });
  }
}

/**
 * Configurar el Modal de Edición / Creación
 */
function setupEditorModal() {
  const overlay = document.getElementById('adminEditorOverlay');
  const closeBtn = document.getElementById('btnEditorClose');
  const cancelBtn = document.getElementById('btnCancelEditor');
  const dropzone = document.getElementById('adminImageDropzone');
  const fileInput = document.getElementById('editImageFileInput');
  const form = document.getElementById('productEditorForm');

  function closeEditor() {
    if (overlay) overlay.classList.remove('active');
    selectedImageFile = null;
    editingProductId = null;
  }

  if (closeBtn) closeBtn.addEventListener('click', closeEditor);
  if (cancelBtn) cancelBtn.addEventListener('click', closeEditor);

  // Control de cambio de categoría (Mostrar campo si es 'otro')
  const categorySelect = document.getElementById('editCategory');
  const customCatWrapper = document.getElementById('customCategoryWrapper');
  const customCatInput = document.getElementById('editCategoryCustom');

  if (categorySelect && customCatWrapper) {
    categorySelect.addEventListener('change', () => {
      if (categorySelect.value === 'otro') {
        customCatWrapper.style.display = 'block';
        if (customCatInput) customCatInput.focus();
      } else {
        customCatWrapper.style.display = 'none';
      }
    });
  }

  // Dropzone de Imagen
  if (dropzone && fileInput) {
    dropzone.addEventListener('click', () => fileInput.click());

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.style.borderColor = 'var(--color-primary)';
    });

    dropzone.addEventListener('dragleave', () => {
      dropzone.style.borderColor = '#CBD5E1';
    });

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.style.borderColor = '#CBD5E1';
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelection(e.dataTransfer.files[0]);
      }
    });

    fileInput.addEventListener('change', (e) => {
      if (e.target.files && e.target.files[0]) {
        handleFileSelection(e.target.files[0]);
      }
    });
  }

  function handleFileSelection(file) {
    selectedImageFile = file;
    const previewWrapper = document.getElementById('imagePreviewWrapper');
    const previewImg = document.getElementById('imagePreviewImg');

    const reader = new FileReader();
    reader.onload = (e) => {
      if (previewImg) previewImg.src = e.target.result;
      if (previewWrapper) previewWrapper.style.display = 'block';
    };
    reader.readAsDataURL(file);
  }

  // Guardar Producto
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const saveBtn = document.getElementById('btnSaveProduct');

      try {
        saveBtn.disabled = true;
        saveBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Guardando...';

        // Categoría (Estándar o personalizada)
        let categoryValue = document.getElementById('editCategory')?.value || 'venta';
        if (categoryValue === 'otro') {
          const customText = document.getElementById('editCategoryCustom')?.value.trim();
          categoryValue = customText ? customText.toLowerCase() : 'otro';
        }

        // Título y Descripción con fallbacks seguros si no se completan
        const titleValue = document.getElementById('editTitle')?.value.trim() || 'Solución Industrial SERPROMAQ';
        const descValue = document.getElementById('editDescription')?.value.trim() || 'Solución y maquinaria industrial garantizada por SERPROMAQ S.A.S.';
        const availabilityValue = document.getElementById('editAvailability')?.value || 'Disponible en Stock';

        // Imagen (Opcional - asigna fotografía por defecto si no subió ninguna)
        let imageUrl = document.getElementById('editImageUrl')?.value;
        if (selectedImageFile) {
          imageUrl = await uploadProductImage(selectedImageFile);
        }
        if (!imageUrl) {
          imageUrl = '/images/electric-forklift.jpg';
        }

        const rawFeatures = document.getElementById('editFeatures')?.value || '';
        const featuresArray = rawFeatures
          .split('\n')
          .map(f => f.trim())
          .filter(f => f.length > 0);

        const productPayload = {
          id: editingProductId,
          title: titleValue,
          category: categoryValue,
          badgeText: document.getElementById('editBadgeText')?.value || 'EQUIPO SERPROMAQ',
          badgeClass: 'badge-green',
          highlightTag: document.getElementById('editHighlightTag')?.value || 'Garantía SERPROMAQ',
          availability: availabilityValue,
          image: imageUrl,
          description: descValue,
          features: featuresArray,
          specs: {
            capacidad: document.getElementById('specCapacidad')?.value || 'Estándar',
            altura: document.getElementById('specAltura')?.value || 'Ajustable',
            motor: document.getElementById('specMotor')?.value || 'Industrial',
            garantia: document.getElementById('specGarantia')?.value || 'Garantía Total'
          }
        };

        await saveProduct(productPayload, !!editingProductId);
        showToast(editingProductId ? '¡Producto actualizado con éxito!' : '¡Nuevo producto agregado al catálogo!', 'success');
        closeEditor();
        renderDashboardView();
      } catch (err) {
        console.error(err);
        showToast('Error al guardar el producto', 'error');
      } finally {
        saveBtn.disabled = false;
        saveBtn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Guardar Producto';
      }
    });
  }
}

/**
 * Abrir el modal de edición de producto
 */
function openProductEditor(productId = null) {
  const overlay = document.getElementById('adminEditorOverlay');
  const titleElem = document.getElementById('editorModalTitle');
  const form = document.getElementById('productEditorForm');
  const previewWrapper = document.getElementById('imagePreviewWrapper');
  const previewImg = document.getElementById('imagePreviewImg');
  const customCatWrapper = document.getElementById('customCategoryWrapper');
  const customCatInput = document.getElementById('editCategoryCustom');

  if (!overlay || !form) return;

  editingProductId = productId;
  selectedImageFile = null;

  const standardCategories = ['venta', 'alquiler', 'mantenimiento', 'repuestos'];

  if (productId) {
    const item = getLoadedCatalogItems().find(i => i.id === productId);
    if (!item) return;

    if (titleElem) titleElem.innerHTML = `<i class="fa-solid fa-pen-to-square" style="color: var(--color-primary);"></i> Editar: ${item.title}`;
    
    document.getElementById('editTitle').value = item.title || '';
    
    // Categoría
    if (standardCategories.includes(item.category)) {
      document.getElementById('editCategory').value = item.category || 'venta';
      if (customCatWrapper) customCatWrapper.style.display = 'none';
      if (customCatInput) customCatInput.value = '';
    } else {
      document.getElementById('editCategory').value = 'otro';
      if (customCatWrapper) customCatWrapper.style.display = 'block';
      if (customCatInput) customCatInput.value = item.category || '';
    }

    document.getElementById('editBadgeText').value = item.badgeText || '';
    document.getElementById('editHighlightTag').value = item.highlightTag || '';
    document.getElementById('editAvailability').value = item.availability || 'Disponible en Stock';
    document.getElementById('editDescription').value = item.description || '';
    document.getElementById('editImageUrl').value = item.image || '';

    const featuresElem = document.getElementById('editFeatures');
    if (featuresElem) {
      featuresElem.value = (item.features && Array.isArray(item.features)) ? item.features.join('\n') : '';
    }

    const specs = item.specs || {};
    document.getElementById('specCapacidad').value = specs.capacidad || '';
    document.getElementById('specAltura').value = specs.altura || '';
    document.getElementById('specMotor').value = specs.motor || '';
    document.getElementById('specGarantia').value = specs.garantia || '';

    if (item.image) {
      if (previewImg) previewImg.src = item.image;
      if (previewWrapper) previewWrapper.style.display = 'block';
    } else {
      if (previewWrapper) previewWrapper.style.display = 'none';
    }
  } else {
    if (titleElem) titleElem.innerHTML = `<i class="fa-solid fa-plus-circle" style="color: var(--color-primary);"></i> Agregar Nuevo Equipo / Insumo`;
    form.reset();
    document.getElementById('editCategory').value = 'venta';
    if (customCatWrapper) customCatWrapper.style.display = 'none';
    if (customCatInput) customCatInput.value = '';
    document.getElementById('editImageUrl').value = '';
    const featuresElem = document.getElementById('editFeatures');
    if (featuresElem) featuresElem.value = '';
    if (previewWrapper) previewWrapper.style.display = 'none';
  }

  overlay.classList.add('active');
}

/**
 * Toast / Alertas visuales flotantes
 */
function showToast(message, type = 'success') {
  let toastContainer = document.getElementById('adminToastContainer');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'adminToastContainer';
    toastContainer.style.cssText = `
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;
    document.body.appendChild(toastContainer);
  }

  const toast = document.createElement('div');
  const bg = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#2563EB';
  const icon = type === 'success' ? 'fa-circle-check' : type === 'error' ? 'fa-triangle-exclamation' : 'fa-circle-info';

  toast.style.cssText = `
    background: ${bg};
    color: #FFFFFF;
    padding: 0.85rem 1.35rem;
    border-radius: 12px;
    font-size: 0.885rem;
    font-weight: 700;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    gap: 0.65rem;
    animation: cardFadeScaleIn 0.3s ease;
  `;
  toast.innerHTML = `<i class="fa-solid ${icon}"></i> ${message}`;

  toastContainer.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, 3500);
}
